import { useEffect, useState } from "react";

import type { Session } from "../auth";

interface Props {
  session: Session;
  onBack: () => void;
  onToast: (msg: string) => void;
  onChangePassword: () => void;
}

export function Settings({ session, onBack, onToast, onChangePassword }: Props) {
  const [supported, setSupported] = useState<boolean | null>(null);
  const [enrolled, setEnrolled] = useState(session.faceIdEnrolled());
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    void session.faceIdSupported().then(setSupported);
  }, [session]);

  async function enable() {
    setError(null);
    setBusy(true);
    try {
      await session.enableFaceId();
      setEnrolled(true);
      onToast("Face ID włączone");
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  }

  function disable() {
    if (!confirm("Wyłączyć Face ID? Przy następnym odblokowaniu trzeba będzie wpisać hasło główne.")) return;
    session.disableFaceId();
    setEnrolled(false);
    onToast("Face ID wyłączone");
  }

  return (
    <>
      <div className="header">
        <button className="back-btn" onClick={onBack} aria-label="Wstecz">
          ‹ Wstecz
        </button>
        <h1>Ustawienia</h1>
      </div>

      <div className="main">
        <div className="section-label">Bezpieczeństwo</div>

        <div className="field-card">
          <div className="field-text">
            <div className="field-label">Face ID / Touch ID</div>
            <div className="field-value text" style={{ fontSize: 14 }}>
              {supported === null
                ? "Sprawdzam dostępność…"
                : supported === false
                ? "Niedostępne na tym urządzeniu (wymaga iOS 17.4+ Safari)"
                : enrolled
                ? "Włączone — sejf odblokujesz Face ID-em"
                : "Wyłączone — sejf odblokujesz hasłem głównym"}
            </div>
          </div>
          {supported && (
            enrolled ? (
              <button
                type="button"
                className="copy-btn"
                onClick={disable}
                style={{ color: "var(--danger)", borderColor: "var(--danger)" }}
              >
                Wyłącz
              </button>
            ) : (
              <button
                type="button"
                className="copy-btn"
                onClick={enable}
                disabled={busy}
              >
                {busy ? "…" : "Włącz"}
              </button>
            )
          )}
        </div>

        {error && <div className="error">{error}</div>}

        <div className="muted" style={{ fontSize: 12, marginTop: 8, lineHeight: 1.5 }}>
          Face ID generuje deterministyczny klucz w Secure Enclave Twojego iPhone-a,
          którym lokalnie szyfrowany jest klucz sejfu. Bez Face ID nie da się
          odszyfrować — serwer nadal niczego nie widzi.
        </div>

        <div className="section-label" style={{ marginTop: 24 }}>Hasło główne</div>
        <div className="field-card">
          <div className="field-text">
            <div className="field-label">Zmień hasło główne</div>
            <div className="field-value text" style={{ fontSize: 14 }}>
              Zmienia klucz szyfrujący sejf. Inne urządzenia będą musiały się zalogować ponownie.
            </div>
          </div>
          <button type="button" className="copy-btn" onClick={onChangePassword}>
            Zmień
          </button>
        </div>
      </div>
    </>
  );
}
